import * as React from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, Text } from '@clutter/clean';

import { XButton } from '@root/components/icons/x_button';

import { AspectRatio } from '@shared/aspect_ratio';

import checkMark from '@images/icons/checkmark-white.svg';
import versus from '@images/illustrations/versus.svg';

import { ImageDescriptor } from '../types/image_descriptor';

type CardData = {
  title: React.ReactNode;
  description: React.ReactNode;
  badge: string;
  items: React.ReactNode[];
  image: ImageDescriptor;
};

const ImageContainer = styled.div`
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  right: 0;
  top: 0;
  width: 100%;
  display: none;

  @media (min-width: 1180px) {
    display: block;
  }
`;

const Card = ({ data, kind }: { data: CardData; kind: 'good' | 'bad' }) => {
  const {
    icon: ListIcon,
    iconBackground,
    flairBackground,
    flairText,
  } = kind === 'good'
    ? {
        icon: <img src={checkMark} alt="" loading="lazy" />,
        iconBackground: COLORS.tealJungle,
        flairBackground: COLORS.tealBackground,
        flairText: COLORS.tealJungle,
      }
    : {
        icon: <XButton width="11px" height="11px" fillColor={COLORS.cloud} />,
        iconBackground: COLORS.toucan,
        flairBackground: COLORS.flower,
        flairText: COLORS.toucan,
      };
  return (
    <Box
      width={['100%', null, '360px']}
      padding={['24px', '32px']}
      margin={kind === 'good' ? '0 0 0 auto' : undefined}
      background={COLORS.cloud}
      border="1px solid"
      borderColor={COLORS.grayBorder}
      borderRadius="4px"
    >
      <Box
        display="inline-block"
        height="28px"
        borderRadius="4px"
        padding="5px 24px"
        margin="0 0 8px"
        background={flairBackground}
        color={flairText}
      >
        <Text.SmallCaps>{data.badge}</Text.SmallCaps>
      </Box>
      <Text.Title size="small">{data.title}</Text.Title>
      <Box margin="12px 0 20px">
        <Text.Body>{data.description}</Text.Body>
      </Box>
      <Box.Flex gap="12px" flexDirection="column">
        {data.items.map((item, i) => (
          <Box.Flex key={i} gap="8px">
            <Box
              background={iconBackground}
              borderRadius="50%"
              width="22px"
              height="22px"
              textAlign="center"
              padding={kind === 'good' ? '2px' : '1px'}
            >
              {ListIcon}
            </Box>
            <div>{item}</div>
          </Box.Flex>
        ))}
      </Box.Flex>
    </Box>
  );
};

export const ComparisonCards: React.FC<{
  background?: (typeof COLORS)['cloud' | 'grayBackground' | 'tealBackground'];
  title: React.ReactNode;
  description: React.ReactNode;
  items: [CardData, CardData];
  reduceBottomPadding?: boolean;
}> = ({
  title,
  description,
  items,
  background = COLORS.cloud,
  reduceBottomPadding,
}) => {
  const [left, right] = items;

  return (
    <Box
      background={background}
      padding={
        reduceBottomPadding
          ? ['48px 0', '80px 0']
          : ['48px 0 24px 0', '80px 0 28px 0']
      }
    >
      <Box margin="0 auto" padding="0 24px" maxWidth="1440px">
        <Box.Flex>
          <Box.FlexItem flexGrow={1} position="relative">
            <ImageContainer>
              <AspectRatio ratio={280 / 483}>
                <img src={left.image.src} alt="" loading="lazy" />
              </AspectRatio>
            </ImageContainer>
          </Box.FlexItem>
          <Box.FlexItem
            width="100%"
            maxWidth="500px"
            textAlign="center"
            padding="0 24px"
          >
            <Text.Title size="medium">{title}</Text.Title>
            <Text.Body>{description}</Text.Body>
          </Box.FlexItem>
          <Box.FlexItem
            flexGrow={1}
            position="relative"
            display={['none', null, 'block']}
          >
            <ImageContainer>
              <AspectRatio ratio={280 / 483}>
                <img src={right.image.src} alt="" loading="lazy" />
              </AspectRatio>
            </ImageContainer>
          </Box.FlexItem>
        </Box.Flex>
        <Box.Flex
          position="relative"
          style={{ zIndex: 1 }}
          justifyContent="center"
          margin={['36px 0 0', null, '24px 0 0']}
          flexDirection={['column', 'row']}
        >
          <Box.FlexItem>
            <Card data={left} kind="good" />
          </Box.FlexItem>
          <Box.FlexItem
            padding={['24px', null, '48px 0 0']}
            textAlign="center"
            minWidth={[null, '100px', '140px', '260px']}
          >
            <img src={versus} alt="versus" loading="lazy" />
          </Box.FlexItem>
          <Box.FlexItem>
            <Card data={right} kind="bad" />
          </Box.FlexItem>
        </Box.Flex>
      </Box>
    </Box>
  );
};
